import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Eye, EyeOff, ScanLine, Globe, ShieldCheck, Fingerprint, KeyRound, ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { http, apiError } from "@/lib/api";
import Captcha from "@/components/Captcha";

const FEATURES = [
  { icon: ScanLine, title: "Continuous Scanning", tags: "SCA · IaC · secrets" },
  { icon: Globe, title: "Live Web Integrity", tags: "defacement · cloaking · TLS" },
  { icon: ShieldCheck, title: "Evidence & Access", tags: "MFA · audit trail · RBAC" },
];

const Field = ({ label, children }) => (
  <>
    <label className="text-[11px] uppercase tracking-[0.14em] text-[#8A8175] font-bold">{label}</label>
    {children}
  </>
);
const inputCls = "w-full mt-1.5 mb-5 bg-[#FDFBF8] border border-[#E7D9C6] rounded-lg px-3.5 py-3 text-sm placeholder:text-[#C4BBAD] focus:outline-none focus:border-[#A5300F] focus:bg-white transition-colors";

export default function Login() {
  const { login, mfaSetup, mfaEnable, mfaVerify } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials"); // credentials | mfa-setup | mfa-verify | mfa-backup | forgot | forgot-done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0); // remount widget to get a fresh challenge
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null); // {secret, qr_png}
  const [backupCodes, setBackupCodes] = useState([]);

  const resetCaptcha = () => { setCaptcha(null); setCaptchaKey((k) => k + 1); };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const data = await login(email.trim(), password, captcha);
      setMfaToken(data.mfa_token);
      if (data.mfa_setup_required) {
        const s = await mfaSetup(data.mfa_token);
        setSetup(s);
        setStep("mfa-setup");
      } else {
        setStep("mfa-verify");
      }
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || "Login failed");
      resetCaptcha();
    } finally { setBusy(false); }
  };

  const submitEnable = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const data = await mfaEnable(mfaToken, code.trim());
      setBackupCodes(data.backup_codes || []);
      setStep("mfa-backup");
    } catch (e2) { setErr(apiError(e2.response?.data?.detail) || "Incorrect code"); }
    finally { setBusy(false); }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await mfaVerify(mfaToken, code.trim()); navigate("/"); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail) || "Incorrect code"); }
    finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await http.post("/auth/forgot-password", { email: email.trim() }); setStep("forgot-done"); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  const copyBackup = () => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Backup codes copied"); };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBFB] text-[#323232]">
      <div className="h-1 w-full" style={{ background: "linear-gradient(90deg,#A5300F 0%,#D55816 45%,#E39629 100%)" }} />
      <header className="flex items-center gap-3 px-8 lg:px-14 pt-7">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center font-display font-bold text-lg text-white shrink-0 shadow-sm" style={{ background: "linear-gradient(135deg,#A5300F,#D55816)" }}>CL</div>
        <div>
          <div className="font-display font-bold text-xl leading-none" style={{ fontVariant: "small-caps" }}>
            <span style={{ color: "#A5300F" }}>Cyber</span><span style={{ color: "#E39629" }}>Lens</span>
          </div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-[#B5AC9E] font-semibold mt-1.5">a CyberNxt platform</div>
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-2 items-center gap-10 px-8 lg:px-14 py-10 max-w-[1400px] w-full mx-auto">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 rounded-full mb-7" style={{ color: "#A5300F", background: "#FFF5EB", border: "1px solid #F0DAC2" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#A5300F]" /> Enterprise Exposure Management
          </span>
          <h1 className="font-display font-bold leading-[1.05] text-5xl lg:text-6xl tracking-tight">
            Your entire<br />attack surface,<br /><span style={{ color: "#A5300F" }}>under </span><span style={{ color: "#E39629" }}>one lens.</span>
          </h1>
          <p className="text-[15px] leading-relaxed text-[#6C655B] mt-6 max-w-lg">
            From the dependencies you build on to the routes that carry your traffic, CyberLens brings every exposure into a single view — protected by mandatory multi-factor sign-in.
          </p>
          <div className="grid sm:grid-cols-3 gap-5 mt-10">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "#FFF5EB", border: "1px solid #F0DAC2" }}><f.icon className="w-4 h-4 text-[#A5300F]" /></div>
                <div><div className="text-sm font-bold text-[#323232] leading-tight">{f.title}</div><div className="text-[11px] text-[#8A8175] mt-0.5">{f.tags}</div></div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center lg:justify-end">
          <div className="w-full max-w-md bg-white rounded-2xl border border-[#ECE6DD] shadow-[0_18px_50px_-24px_rgba(50,50,50,0.35)] overflow-hidden">
            <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg,#A5300F,#E39629)" }} />
            <div className="p-8 lg:p-10">
              {err && <div className="mb-5 text-sm text-[#A5300F] bg-[#FFF5EB] border border-[#F0DAC2] rounded-lg px-3.5 py-2.5" data-testid="login-error">{err}</div>}

              {step === "credentials" && (
                <form onSubmit={submitCredentials} data-testid="login-form">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#A5300F] mb-3"><Fingerprint className="w-3.5 h-3.5" /> Secure sign-in</div>
                  <h2 className="font-display text-3xl font-bold">Welcome back.</h2>
                  <p className="text-sm text-[#8A8175] mt-1.5 mb-7">Sign in to your CyberLens workspace.</p>
                  <Field label="Work email">
                    <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus placeholder="you@company.com" className={inputCls} />
                  </Field>
                  <Field label="Password">
                    <div className="relative mt-1.5 mb-5">
                      <input data-testid="login-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••"
                        className="w-full bg-[#FDFBF8] border border-[#E7D9C6] rounded-lg px-3.5 py-3 pr-11 text-sm placeholder:text-[#C4BBAD] focus:outline-none focus:border-[#A5300F] focus:bg-white transition-colors" />
                      <button type="button" onClick={() => setShow(!show)} data-testid="toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B5AC9E] hover:text-[#A5300F]">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    </div>
                  </Field>
                  <div className="mb-4"><Captcha key={captchaKey} onChange={setCaptcha} /></div>
                  <button type="submit" disabled={busy || !email || !password} data-testid="login-submit" className="w-full bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                    {busy ? "Signing in…" : <>Continue <LogIn className="w-4 h-4" /></>}
                  </button>
                  <button type="button" onClick={() => { setErr(""); setStep("forgot"); }} data-testid="forgot-link" className="w-full text-center text-xs text-[#8A8175] hover:text-[#A5300F] mt-5">Forgot your password?</button>
                </form>
              )}

              {step === "mfa-setup" && (
                <form onSubmit={submitEnable} data-testid="mfa-setup-form">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#A5300F] mb-3"><KeyRound className="w-3.5 h-3.5" /> Set up two-factor</div>
                  <h2 className="font-display text-2xl font-bold">Protect your account</h2>
                  <p className="text-sm text-[#8A8175] mt-1.5 mb-5">Scan this QR code with Google Authenticator, Authy, or 1Password, then enter the 6-digit code.</p>
                  {setup?.qr_png && <img src={setup.qr_png} alt="MFA QR" data-testid="mfa-qr" className="w-40 h-40 mx-auto border border-[#ECE6DD] rounded-lg p-2 bg-white" />}
                  <p className="text-[11px] text-center text-[#B5AC9E] mt-2 mb-5 break-all">Or enter key manually: <span className="font-mono text-[#8A8175]">{setup?.secret}</span></p>
                  <Field label="6-digit code">
                    <input data-testid="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" autoFocus className={inputCls} />
                  </Field>
                  <button type="submit" disabled={busy || code.length < 6} data-testid="mfa-enable-submit" className="w-full bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D] disabled:opacity-50">{busy ? "Verifying…" : "Enable & sign in"}</button>
                </form>
              )}

              {step === "mfa-backup" && (
                <div data-testid="mfa-backup">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#2E7D32] mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Two-factor enabled</div>
                  <h2 className="font-display text-2xl font-bold">Save your backup codes</h2>
                  <p className="text-sm text-[#8A8175] mt-1.5 mb-4">Store these somewhere safe. Each code can be used once if you lose your authenticator.</p>
                  <div className="grid grid-cols-2 gap-2 bg-[#FDFBF8] border border-[#ECE6DD] rounded-lg p-4 font-mono text-sm">
                    {backupCodes.map((c) => <div key={c} className="text-[#5C554C]">{c}</div>)}
                  </div>
                  <button onClick={copyBackup} className="w-full mt-3 flex items-center justify-center gap-2 text-xs text-[#8A8175] hover:text-[#A5300F]"><Copy className="w-3.5 h-3.5" /> Copy codes</button>
                  <button onClick={() => navigate("/")} data-testid="mfa-backup-continue" className="w-full mt-4 bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D]">I've saved them — continue</button>
                </div>
              )}

              {step === "mfa-verify" && (
                <form onSubmit={submitVerify} data-testid="mfa-verify-form">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#A5300F] mb-3"><KeyRound className="w-3.5 h-3.5" /> Two-factor</div>
                  <h2 className="font-display text-2xl font-bold">Enter your code</h2>
                  <p className="text-sm text-[#8A8175] mt-1.5 mb-5">Open your authenticator app and enter the current 6-digit code (or a backup code).</p>
                  <Field label="Authentication code">
                    <input data-testid="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="text" placeholder="123456" autoFocus className={inputCls} />
                  </Field>
                  <button type="submit" disabled={busy || code.length < 6} data-testid="mfa-verify-submit" className="w-full bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D] disabled:opacity-50">{busy ? "Verifying…" : "Verify & sign in"}</button>
                  <button type="button" onClick={() => { setStep("credentials"); setCode(""); setErr(""); resetCaptcha(); }} className="w-full flex items-center justify-center gap-1.5 text-xs text-[#8A8175] hover:text-[#A5300F] mt-5"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
                </form>
              )}

              {step === "forgot" && (
                <form onSubmit={submitForgot} data-testid="forgot-form">
                  <h2 className="font-display text-2xl font-bold">Reset your password</h2>
                  <p className="text-sm text-[#8A8175] mt-1.5 mb-5">Enter your email. If an account exists, your administrator can issue you a secure reset link.</p>
                  <Field label="Work email"><input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus placeholder="you@company.com" className={inputCls} /></Field>
                  <button type="submit" disabled={busy || !email} data-testid="forgot-submit" className="w-full bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D] disabled:opacity-50">{busy ? "Sending…" : "Request reset"}</button>
                  <button type="button" onClick={() => { setStep("credentials"); setErr(""); }} className="w-full flex items-center justify-center gap-1.5 text-xs text-[#8A8175] hover:text-[#A5300F] mt-5"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign-in</button>
                </form>
              )}

              {step === "forgot-done" && (
                <div data-testid="forgot-done">
                  <h2 className="font-display text-2xl font-bold">Check with your administrator</h2>
                  <p className="text-sm text-[#8A8175] mt-1.5 mb-5">If an account exists for that email, a reset request has been logged. Your CyberLens administrator can generate a one-time reset link for you.</p>
                  <button onClick={() => { setStep("credentials"); setErr(""); }} className="w-full bg-[#A5300F] text-white font-semibold py-3 rounded-lg hover:bg-[#8F2A0D]">Back to sign-in</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <footer className="px-8 lg:px-14 py-5 border-t border-[#ECE6DD] flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-[#B5AC9E]">
        <span>© 2026 CyberLens · a CyberNxt platform. All rights reserved.</span>
        <span className="tracking-[0.14em] uppercase font-semibold">Authorised use only · All access is logged</span>
      </footer>
    </div>
  );
}
